import { useEffect } from "react";
import { FieldValues, useFormContext } from "react-hook-form";
import { useT } from "@transifex/react";
import { Checkbox } from "storybook/components/checkbox";
import {
  Container,
  CheckList,
  InputsContainer,
  DayTimesContainer,
} from "./styles";

export type WeekDay =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday";

const options = {
  shouldValidate: true,
};

type AvailabilityItemProps = {
  label: string;
  name: WeekDay;
  namePrefix: string;
  theme: "default" | "patient-facing";
};

function AvailabilityItem({
  label,
  name,
  namePrefix,
  theme,
}: AvailabilityItemProps) {
  const t = useT();
  const { setValue, watch } = useFormContext();
  const state = watch(`${namePrefix}${name}`);

  useEffect(() => {
    if (state?.enabled) {
      setValue(`${namePrefix}anytime`, false, options);
    } else {
      setValue(`${namePrefix}${name}.am`, false, options);
      setValue(`${namePrefix}${name}.pm`, false, options);
    }
  }, [state?.enabled, name, namePrefix, setValue]);

  useEffect(() => {
    if (!state?.am && !state?.pm) {
      setValue(`${namePrefix}${name}.enabled`, false, options);
    }
    if (state?.am || state?.pm) {
      setValue(`${namePrefix}${name}.enabled`, true, options);
    }
  }, [state?.am, state?.pm, name, namePrefix, setValue]);

  useEffect(() => {
    if (state?.enabled && !state?.am && !state?.pm) {
      setValue(`${namePrefix}${name}.am`, true, options);
      setValue(`${namePrefix}${name}.pm`, true, options);
    }
  }, [state?.enabled, state?.am, state?.pm, name, namePrefix, setValue]);

  return (
    <div>
      <Checkbox<FieldValues>
        size="small"
        hideDirty={true}
        label={label}
        theme={theme}
        name={`${namePrefix}${name}.enabled`}
      />
      <DayTimesContainer>
        <Checkbox<FieldValues>
          size="small"
          hideDirty={true}
          button={true}
          label={t("AM")}
          theme={theme}
          name={`${namePrefix}${name}.am`}
        />
        <Checkbox<FieldValues>
          size="small"
          hideDirty={true}
          button={true}
          label={t("PM")}
          theme={theme}
          name={`${namePrefix}${name}.pm`}
        />
      </DayTimesContainer>
    </div>
  );
}

export type AvailabilityPickerProps = {
  namePrefix?: string;
  theme?: "default" | "patient-facing";
  noContainer?: boolean;
};

export function AvailabilityPicker({
  namePrefix = "",
  theme = "default",
  noContainer = false,
}: AvailabilityPickerProps) {
  const t = useT();
  const { setValue, watch } = useFormContext();
  const isAnytimeEnabled = watch(`${namePrefix}anytime`);

  useEffect(() => {
    if (isAnytimeEnabled) {
      setValue(`${namePrefix}monday.enabled`, false, options);
      setValue(`${namePrefix}tuesday.enabled`, false, options);
      setValue(`${namePrefix}wednesday.enabled`, false, options);
      setValue(`${namePrefix}thursday.enabled`, false, options);
      setValue(`${namePrefix}friday.enabled`, false, options);
    }
  }, [isAnytimeEnabled, namePrefix, setValue]);

  const content = (
    <InputsContainer data-id="InputsContainer">
      <CheckList>
        <Checkbox<FieldValues>
          size="small"
          hideDirty={true}
          theme={theme}
          name={`${namePrefix}anytime`}
          label={t("Flexible - Available Anytime")}
        />
        <AvailabilityItem
          label={t("Monday")}
          name="monday"
          namePrefix={namePrefix}
          theme={theme}
        />
        <AvailabilityItem
          label={t("Tuesday")}
          name="tuesday"
          namePrefix={namePrefix}
          theme={theme}
        />
        <AvailabilityItem
          label={t("Wednesday")}
          name="wednesday"
          namePrefix={namePrefix}
          theme={theme}
        />
        <AvailabilityItem
          label={t("Thursday")}
          name="thursday"
          namePrefix={namePrefix}
          theme={theme}
        />
        <AvailabilityItem
          label={t("Friday")}
          name="friday"
          namePrefix={namePrefix}
          theme={theme}
        />
      </CheckList>
    </InputsContainer>
  );

  if (noContainer) {
    return content;
  }

  return <Container>{content}</Container>;
}
